import EventClient from "@2u-landing-pages/utils/event-client";
import { logEvent } from "@red-digital/bricks";
import { useEffect, useRef, useState } from "react";

type Steps = {
  current: number | null;
  total: number | null;
};

declare global {
  interface Window {
    cwr: any;
  }
}

export const useFormEvents = (formId: string, leadFormType: "TY" | "LP") => {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [steps, setSteps] = useState<Steps>({
    current: null,
    total: null,
  });

  const getLeadFormStep = () => {
    if (!formContainerRef.current) return;

    const label = formContainerRef.current.querySelector(".progress__label");

    if (!label) {
      setSteps({
        current: 1,
        total: 1,
      });

      return {
        current: 1,
        total: 1,
      };
    }

    const stepString = label?.textContent;
    const regex = /(\d+) of (\d+)/;
    const match = stepString?.match(regex) || [];
    const updatedSteps = {
      current: parseInt(match[1], 10) || 1,
      total: parseInt(match[2], 10) || 1,
    };

    setSteps(updatedSteps);

    return updatedSteps;
  };

  useEffect(() => {
    if (!formContainerRef.current) return;

    const handleBlurEvent = (event: FocusEvent) => {
      const target = event.target as HTMLInputElement;
      const { current } = getLeadFormStep() || {};

      if (
        (target?.tagName === "INPUT" && target?.type === "text") ||
        (target?.tagName === "INPUT" && target?.type === "email")
      ) {
        EventClient.fieldInputted({
          formContext: {
            formId,
          },
          userInputField: {
            autofilled: false,
            fieldId: target.id,
            fieldLabel: target.placeholder,
            fieldName: target.name,
            fieldType: target.type,
            fieldValue: target.value,
          },
          stepContext: {
            stepNumber: current,
          },
        });
      }
    };

    const handleChangeEvent = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const { current } = getLeadFormStep() || {};

      if (
        target?.tagName === "SELECT" ||
        (target?.tagName === "INPUT" && target?.type !== "text" && target?.type !== "email" && target?.type !== "password") ||
        target?.type === "radio" ||
        target?.type === "checkbox"
      ) {
        EventClient.fieldInputted({
          formContext: {
            formId,
          },
          userInputField: {
            autofilled: false,
            fieldId: target.id,
            fieldLabel: target.placeholder,
            fieldName: target.name,
            fieldType: target.type,
            fieldValue: target.value,
          },
          stepContext: {
            stepNumber: current,
          },
        });
      }
    };

    const handleClickEvent = (event: MouseEvent) => {
      const target = event.target as HTMLInputElement | HTMLAnchorElement;
      const { current: currentStep, total: totalSteps } = getLeadFormStep() || {};

      if (
        target?.classList.contains("lead-form-next-step") ||
        (target?.classList.contains("button") && target?.closest(".form-get-info")) ||
        (target?.classList.contains("form-button") && target?.closest(".multi-step-form"))
      ) {
        const { innerText } = target;

        // wait for the form to update before checking for errors
        setTimeout(() => {
          if (!formContainerRef.current) return;

          const inputErrors = Array.from(
            formContainerRef.current.querySelectorAll(
              "input.error, select.error, .form-group.is--invalid, .form-field-group.invalid"
            )
          );

          if (inputErrors.length) {
            const errorMessages = formContainerRef.current.querySelectorAll(".is--invalid");
            const errorMessage =
              Array.from(errorMessages)
                .map((el) => el.textContent)
                .join(", ") ?? "";

            EventClient.formErrored({
              errorMessage,
              stepContext: {
                stepNumber: currentStep as number,
              },
            });

            return;
          }

          EventClient.elementClicked({
            webElement: {
              htmlId: formId,
              text: innerText || "",
              position: `Step: ${currentStep}`,
              location: "Landing Page",
            },
          });

          const complete = currentStep === totalSteps;

          EventClient.formSubmitted({
            formContext: {
              formId,
            },
            stepContext: {
              stepNumber: leadFormType === "TY" ? "TY Page" : currentStep as number,
              stepName: complete ? "COMPLETED" : "",
            },
          });

          if (complete) {
            logEvent("form_completed", {
              formId,
              complete: true,
            });
          }

          logEvent("form_next_button", {
            formId,
            currentStep,
            totalSteps,
          });
        });
      } else if (target?.tagName === "A") {
        EventClient.elementClicked({
          webElement: {
            elementType: "Landing Page",
            htmlId: (target as HTMLAnchorElement).href,
            location: "LoadForm",
            name: target.innerText,
            position: "",
            text: "",
          },
        });
      }
    };

    formContainerRef.current.addEventListener("blur", handleBlurEvent, true);
    formContainerRef.current.addEventListener("change", handleChangeEvent, true);
    formContainerRef.current.addEventListener("click", handleClickEvent, true);

    return () => {
      if (!formContainerRef.current) return;

      formContainerRef.current.removeEventListener("blur", handleBlurEvent, true);
      formContainerRef.current.removeEventListener("change", handleChangeEvent, true);
      formContainerRef.current.removeEventListener("click", handleClickEvent, true);
    };
  }, []);

  return {
    formContainerRef,
    steps,
  };
};
